import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DataCard,
  Icon,
  IconBlock,
  Link as ANFLink,
} from 'anf-core-react';

import TmntText from '../../components/tmnt-text/tmnt-text';
import DropOffLocationQRCode from '../../components/drop-off-location-qr-code/drop-off-location-qr-code';
import MapLink from './map-link';
import DistributionCenterAddress from './distribution-center-address';

export default function NextStepsSection({
  tmnts = {},
  carrier = {},
  confirmationDetails = {},
  returnedOrder = {},
}) {
  const {
    isShipMySelf,
    showRETNumberOnConfirmation,
    isQRCode,
    canShowQRCode,
    canShowLabel,
    qrCodeRMANo,
    retNumber,
    qrCodeReturnDetailsTmnt,
    qrCodeReturnDetailsMoreTmnt,
    canShowRefundInitiatedDetails,
    canShowEmailNotificationDetails,
    canShowExchangeOrderShipDetails,
    canShowReturnExchangeInitiatedDetails,
    canShowReturnExchangeOrderShipDetails,
    canShowReturnInStoreRefundInitiatedDetailsOPM,
    canShowReturnInStoreRefundInitiatedDetailsMC,
    dropOffDate,
    dropOffDateLong,
    canShowItemDeliveredAndProcessedDetails,
    canShowDistributionCenterReturnAddress,
    canShowMapLink,
  } = confirmationDetails;

  const { encodedOrderDetails } = returnedOrder;

  const mapLink = useMemo(() => {
    if (!canShowMapLink || !carrier?.showMapLinkOnConfirmation) {
      return null;
    }

    return (
      <MapLink
        carrier={carrier}
      />
    );
  }, [canShowMapLink, carrier]);

  const distributionCenterAddress = useMemo(() => {
    if (!canShowDistributionCenterReturnAddress) {
      return null;
    }

    return (
      <DistributionCenterAddress
        carrier={carrier}
      />
    );
  }, [canShowDistributionCenterReturnAddress, carrier]);

  const qrCodeReturnDetailsReplacements = useMemo(() => {
    if (carrier.type === 'returninstore') {
      return { 0: `<strong>${dropOffDateLong}</strong>` };
    }
    return { 0: carrier.name };
  }, [carrier, dropOffDateLong]);

  const linkLabelText = useMemo(() => {
    const dropoffLocationsLinkChild = carrier?.children?.find(child => Boolean(child.dropoffLocationsLink));
    return dropoffLocationsLinkChild?.dropoffLocationsLink;
  }, [carrier]);

  return (
    <div
      className="exchange-confirmation-view-next-steps"
      data-property="DRE_NEXT_STEPS"
      data-testid="next-steps"
    >
      <DataCard
        headerTitle={tmnts.DRE_NEXT_STEPS?.value}
        variant="bordered"
      >
        {(canShowLabel) && (
          <IconBlock alignIcon="top" icon={<Icon icon="returns" size="m" />}>
            {!isShipMySelf && (<TmntText k="DRE_PRINT_YOUR_LABEL_TEXT" replacements={{ 0: carrier.name }} />)}
            {isShipMySelf && (<TmntText k="DRE_SHIPMYSELF_PACK_ITEMS_AND_BRING_TO_CARRIER" />)}
            {isShipMySelf && (<TmntText k="DRE_SHIP_INVOICE_ORDER_CONFIRMATION_DETAILS" />)}
            {isShipMySelf && (<TmntText className="send-address-date" k="DRE_SEND_BACK_TO_ADDRESS_BEFORE_DATE" replacements={{ 0: dropOffDate }} />)}
            {distributionCenterAddress}
          </IconBlock>
        )}

        {isQRCode && (
          <>
            <IconBlock alignIcon="top" icon={<Icon icon="return" size="m" />}>
              {qrCodeReturnDetailsTmnt && (
                <TmntText
                  k={qrCodeReturnDetailsTmnt}
                  replacements={qrCodeReturnDetailsReplacements}
                  html
                />
              )}
              {qrCodeReturnDetailsMoreTmnt && (
                <TmntText
                  k={qrCodeReturnDetailsMoreTmnt}
                  className="qr-code-return-details-more-text"
                />
              )}
              {carrier.purolatorQRCodeServiceDetailsText && (
              <div className="link-wrapper">
                {carrier.purolatorQRCodeServiceDetailsText}
              </div>
              )}
              {carrier.purolatorSearchEligibleDropOffText
                ? (
                  <div className="link-wrapper">
                    <ANFLink href={carrier.mapUrl} target="_blank">
                      {carrier.purolatorSearchEligibleDropOffText}
                    </ANFLink>
                    <div className="external-link-icon">
                      <Icon
                        icon="external-link-anf"
                        labelText="external-link-anf"
                      />
                    </div>
                  </div>
                )
                : mapLink}
            </IconBlock>
            {canShowQRCode && qrCodeRMANo && (
              <DropOffLocationQRCode
                rmaNumber={qrCodeRMANo}
                searchUrl={carrier.mapUrl}
              />
            )}
            {canShowQRCode && encodedOrderDetails && (
              <DropOffLocationQRCode
                rmaNumber={encodedOrderDetails}
                showRmaNumber={false}
                searchUrl={carrier.mapUrl}
                linkLabelText={linkLabelText}
              />
            )}
          </>
        )}

        <IconBlock alignIcon="top" icon={<Icon icon="credit" size="m" />}>
          {canShowRefundInitiatedDetails && (<TmntText k="DRE_REFUND_INITIATED_DETAILS" />)}
          {canShowEmailNotificationDetails && (<TmntText k="DRE_EMAIL_NOTIFICATION_DETAILS" />)}
          {canShowExchangeOrderShipDetails && (<TmntText k="DRE_EXCHANGE_ORDER_SHIP_DETAILS" />)}
          {canShowReturnExchangeInitiatedDetails && (<TmntText k="DRE_RETURN_EXCHANGE_INITIATED_DETAILS" />)}
          {canShowReturnExchangeOrderShipDetails && (<TmntText k="DRE_RETURN_EXCHANGE_ORDER_SHIP_DETAILS" />)}
          {canShowItemDeliveredAndProcessedDetails && (<TmntText k="DRE_ITEM_DELIVERED_AND_PROCESSED_DETAILS" />)}
          {canShowReturnInStoreRefundInitiatedDetailsOPM && (<TmntText k="DRE_OPM_RETURN_IN_STORE" />)}
          {canShowReturnInStoreRefundInitiatedDetailsMC && (<TmntText k="DRE_MC_RETURN_IN_STORE" />)}
        </IconBlock>
        {showRETNumberOnConfirmation && retNumber && (
          <>
            <TmntText k="DRE_DHL_RET_NOTE" />
            <div className="ret-wrapper" data-testid="rma-wrapper">
              <p className="ret-text">
                <TmntText k="DRE_DHL_RET_NUMBER" replacements={{ 0: retNumber }} />
              </p>
            </div>
          </>
        )}
      </DataCard>
    </div>
  );
}

NextStepsSection.propTypes = {
  tmnts: PropTypes.shape({
    DRE_NEXT_STEPS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_RMA_NUMBER: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_REFUND_INITIATED_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_EXCHANGE_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_RETURN_AND_EXCHANGE_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_ITEM_DELIVERED_AND_PROCESSED_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_SHIPMYSELF_PACK_ITEMS_AND_BRING_TO_CARRIER: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_SEND_BACK_TO_ADDRESS_BEFORE_DATE: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_PRINT_LABEL: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_PUROLATOR_QRCODE_SERVICE_DETAILS_TEXT: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_PUROLATOR_SEARCH_ELIGIBLE_DROP_OFF_TEXT: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_PRINT_YOUR_LABEL_TEXT: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_SHIP_INVOICE_ORDER_CONFIRMATION_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_EMAIL_NOTIFICATION_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_EXCHANGE_ORDER_SHIP_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_RETURN_EXCHANGE_INITIATED_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_RETURN_EXCHANGE_ORDER_SHIP_DETAILS: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_DHL_RET_NOTE: PropTypes.shape({
      value: PropTypes.string,
    }),
    DRE_DHL_RET_NUMBER: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  carrier: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    showMapLinkOnConfirmation: PropTypes.bool,
    children: PropTypes.array,
    purolatorQRCodeServiceDetailsText: PropTypes.string,
    purolatorSearchEligibleDropOffText: PropTypes.string,
    mapUrl: PropTypes.string,
  }),
  confirmationDetails: PropTypes.shape({
    isShipMySelf: PropTypes.bool,
    showRETNumberOnConfirmation: PropTypes.bool,
    isQRCode: PropTypes.bool,
    canShowQRCode: PropTypes.bool,
    canShowLabel: PropTypes.bool,
    qrCodeRMANo: PropTypes.string,
    retNumber: PropTypes.string,
    qrCodeReturnDetailsTmnt: PropTypes.string,
    canShowRefundInitiatedDetails: PropTypes.bool,
    canShowEmailNotificationDetails: PropTypes.bool,
    canShowExchangeOrderShipDetails: PropTypes.bool,
    canShowReturnExchangeInitiatedDetails: PropTypes.bool,
    canShowReturnExchangeOrderShipDetails: PropTypes.bool,
    canShowReturnInStoreRefundInitiatedDetailsOPM: PropTypes.bool,
    canShowReturnInStoreRefundInitiatedDetailsMC: PropTypes.bool,
    dropOffDate: PropTypes.string,
    dropOffDateLong: PropTypes.string,
    canShowItemDeliveredAndProcessedDetails: PropTypes.bool,
    canShowDistributionCenterReturnAddress: PropTypes.bool,
    canShowMapLink: PropTypes.bool,
  }),
  returnedOrder: PropTypes.shape({
    encodedOrderDetails: PropTypes.string,
  }),
}; 